import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import {
  ICustomNotification,
  ICustomNotificationMessage,
  ICustomNotificationsSplitData,
  IMessage,
  INotificationSplit,
  NotificationType
} from '@app/settings/models';
import { CustomNotificationsRepoService } from './custom-notifications-repo.service';
import { splitTime } from '@settings/helpers';

@Injectable({
  providedIn: 'root'
})
export class CustomNotificationsService {
  constructor(private _customNotificationsRepo: CustomNotificationsRepoService) {}

  getCustomNotifications(selectedMonth: Date): Observable<ICustomNotificationsSplitData> {
    return this._customNotificationsRepo
      .getCustomNotifications({
        year: selectedMonth.getFullYear().toString(10),
        month: (selectedMonth.getMonth() + 1).toString(10)
      })
      .pipe(map((customNotificationsData) => this._splitCustomNotifications(customNotificationsData)));
  }

  postCustomNotifications(
    selectedMonth: Date,
    notifications: INotificationSplit[],
    messages: IMessage[],
    setDefault: boolean,
    types: NotificationType[]
  ): Observable<undefined> {
    const year = selectedMonth.getFullYear().toString(10);
    const month = (selectedMonth.getMonth() + 1).toString(10);
    return this._customNotificationsRepo.postCustomNotifications(
      year,
      month,
      this._joinCustomNotifications(notifications, messages),
      setDefault,
      types
    );
  }

  restoreNotification(selectedMonth: Date, type: NotificationType): Observable<ICustomNotificationsSplitData> {
    return this._customNotificationsRepo
      .restoreNotification({
        year: selectedMonth.getFullYear().toString(10),
        month: (selectedMonth.getMonth() + 1).toString(10),
        type
      })
      .pipe(map((customNotifications) => this._splitCustomNotifications(customNotifications)));
  }

  private _splitCustomNotifications(customNotifications: ICustomNotification): ICustomNotificationsSplitData {
    const { restorable, changeable, deadlineDate, messages, customNotificationDateTimes } = {
      ...customNotifications
    };
    const deadlineMessage = messages.find(({ type }) => type === NotificationType.DEADLINE);
    const overviewMessage = messages.find(({ type }) => type === NotificationType.OVERVIEW);
    const normalizedMessages: IMessage[] = [];
    const notifications: INotificationSplit[] = customNotificationDateTimes.map(({ daysOffset, time, type }) => {
      return { daysOffset: Math.abs(daysOffset), ...splitTime(time), type };
    });

    if (deadlineMessage) {
      const { subject, message, messageDefault, subjectDefault, type } = deadlineMessage;
      normalizedMessages.push({
        text: message,
        isMessageDefault: messageDefault && subjectDefault,
        notificationType: type,
        subject
      });
    } else {
      normalizedMessages.push({
        text: '',
        subject: '',
        isMessageDefault: false,
        notificationType: NotificationType.DEADLINE
      });
    }

    if (overviewMessage) {
      const { subject, message, messageDefault, subjectDefault, type } = overviewMessage;
      normalizedMessages.push({
        text: message,
        isMessageDefault: messageDefault && subjectDefault,
        notificationType: type,
        subject
      });
    } else {
      normalizedMessages.push({
        text: '',
        subject: '',
        isMessageDefault: false,
        notificationType: NotificationType.OVERVIEW
      });
    }

    return {
      messages: normalizedMessages,
      notifications,
      restorable,
      changeable,
      deadlineDate
    };
  }

  private _joinCustomNotifications(notifications: INotificationSplit[], messages: IMessage[]): ICustomNotification {
    const customNotificationDateTimes = notifications.map(({ hour, minute, period, daysOffset, type }) => {
      const time = `${hour}:${minute} ${period}`;
      daysOffset = -Math.abs(daysOffset);
      return { time, daysOffset, type };
    });
    const normalizedMessages: ICustomNotificationMessage[] = messages.map(
      ({ subject, text, isMessageDefault, notificationType }) => ({
        message: text,
        messageDefault: isMessageDefault,
        subjectDefault: isMessageDefault,
        subject,
        type: notificationType
      })
    );

    return { messages: normalizedMessages, customNotificationDateTimes };
  }
}
